import { useContext } from 'react';
import { Breadcrumb, Button, Space, Tabs } from 'antd';
import { Link } from 'react-router-dom';
import { FaFilePdf, FaTrophy } from 'react-icons/fa';

import Loading from '../../../components/Loading';
import PageHeader from '../../../components/PageHeader';
import GiveawayStatusTag from '../../Giveaways/components/GiveawayStatusTag';
import { ThemeContext } from '../../../contexts/ThemeContext';
import { convertDataUSParaBrasilEHora } from '../../../utils/Conversores';

import useViewGiveawayAuto from './useViewGiveawayAuto';
import GiveawayParticipants from './components/Tabs/Participants';
import Tickets from './components/Tabs/Tickets';
import ReportStatsGiveawayAuto from './components/ReportStatsGiveaway';
import GiveawayAwardsAuto from './components/Tabs/Awards';

export default function ViewGiveawayAuto() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isLoading,
        statsGiveawayAuto,
        statsPreviousGiveawayAuto,
        giveawayAutoData,
        previousGiveawayAutoData,
        handlePrint,
        componentToPrintRef,
        ticketDistribution,
        canViewWinners,
        buttonReportDisabled,
        onChartAnimationEnd,
        activeTab
    } = useViewGiveawayAuto();

    const tabs = [
        {
            key: '1',
            label: 'Estatísticas',
            children: (
                <ReportStatsGiveawayAuto
                    refToPrint={componentToPrintRef}
                    onChartAnimationEnd={onChartAnimationEnd}
                    giveawayAutoData={giveawayAutoData}
                    previousGiveawayAutoData={previousGiveawayAutoData}
                    statsGiveawayAuto={statsGiveawayAuto}
                    statsPreviousGiveawayAuto={statsPreviousGiveawayAuto}
                    ticketDistribution={ticketDistribution}
                    canViewWinners={canViewWinners}
                />
            )
        },
        {
            key: '2',
            label: 'Bilhetes',
            children: <Tickets />
        },
    ];

    if (giveawayAutoData) {
        tabs.push({
            key: '3',
            label: 'Participantes',
            children: <GiveawayParticipants giveawayId={giveawayAutoData.id} />
        });
    }

    tabs.push({
        key: '4',
        label: 'Prêmios',
        children: <GiveawayAwardsAuto />
    });

    const getSubtitle = () => {
        if (!giveawayAutoData) return "Visualização detalhada do sorteio automático e suas estatísticas";
        const parts: string[] = [];
        if (giveawayAutoData.period_beginning && giveawayAutoData.period_ending) {
            parts.push(`Período: ${convertDataUSParaBrasilEHora(giveawayAutoData.period_beginning)} até ${convertDataUSParaBrasilEHora(giveawayAutoData.period_ending)}`);
        }
        if (giveawayAutoData.date) {
            parts.push(`Data do sorteio: ${convertDataUSParaBrasilEHora(giveawayAutoData.date)}`);
        }
        return parts.join(' • ');
    };

    return (
        <Loading loading={isLoading}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* External Breadcrumb Card */}
                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 10,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Breadcrumb
                        items={[
                            {
                                title: <Link to='/sorteios/auto'>Sorteios Automáticos</Link>,
                            },
                            {
                                title: giveawayAutoData?.description || `Visualizar sorteio`,
                            }
                        ]}
                    />
                </div>

                <PageHeader
                    icon={<FaTrophy />}
                    title={giveawayAutoData?.description || "Visualizar Sorteio Automático"}
                    subtitle={getSubtitle()}
                    tag={
                        giveawayAutoData ? (
                            <GiveawayStatusTag
                                status={giveawayAutoData.status}
                                isValid={giveawayAutoData.is_valid}
                            />
                        ) : undefined
                    }
                    actions={
                        giveawayAutoData && (
                            <Space align="center">
                                <Button
                                    onClick={() => handlePrint()}
                                    disabled={buttonReportDisabled}
                                    type='primary'
                                    icon={<FaFilePdf />}
                                    style={{
                                        borderRadius: 8,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontWeight: 500,
                                    }}
                                >
                                    Imprimir relatório
                                </Button>
                            </Space>
                        )
                    }
                />

                <div
                    style={{
                        background: isDark ? '#141414' : '#ffffff',
                        borderRadius: 14,
                        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
                        boxShadow: isDark
                            ? '0 4px 20px rgba(0, 0, 0, 0.35)'
                            : '0 2px 10px rgba(0, 0, 0, 0.03)',
                        padding: '18px 20px',
                    }}
                >
                    <Tabs
                        defaultActiveKey={activeTab}
                        items={tabs}
                    />
                </div>
            </div>
        </Loading>
    );
}
